import { useState, useEffect } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { CalendarDays, Clock, Loader2, ArrowUpRight } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "../ui/dialog";
import { Calendar } from "../ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { api } from "../../lib/api";

const WAIVER_TEXT =
  "I acknowledge that firearms training involves inherent risks, including serious injury or death. " +
  "I confirm I am legally permitted to possess and handle firearms, will follow all range and safety " +
  "rules and instructor directions at all times, and am participating voluntarily. I release Midwest Made " +
  "Firearms Training, its instructors, and affiliates from any and all liability for injury, loss, or damage " +
  "arising from my participation, to the fullest extent permitted by law.";

export default function BookingDialog({ course, timeSlots, trigger, onBooked, initialBooking = null }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(initialBooking ? new Date(initialBooking.date) : undefined);
  const [slot, setSlot] = useState(initialBooking ? initialBooking.time_slot : "");
  const [loading, setLoading] = useState(false);
  const [waiverName, setWaiverName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [blocked, setBlocked] = useState([]);
  const isReschedule = !!initialBooking;

  useEffect(() => {
    if (!date) { setBlocked([]); return; }
    const iso = format(date, "yyyy-MM-dd");
    api.get(`/availability/${iso}`)
      .then((r) => {
        const b = r.data.blocked || [];
        setBlocked(b);
        setSlot((s) => (b.includes(s) ? "" : s));
      })
      .catch(() => setBlocked([]));
  }, [date]);

  const handleSubmit = async () => {
    if (!date || !slot) {
      toast.error("Pick a date and a time slot.");
      return;
    }
    if (!isReschedule && (!agreed || !waiverName.trim())) {
      toast.error("Please sign the liability waiver to continue.");
      return;
    }
    setLoading(true);
    try {
      const iso = format(date, "yyyy-MM-dd");
      if (isReschedule) {
        await api.post(`/bookings/${initialBooking.id}/reschedule`, { date: iso, time_slot: slot });
        toast.success("Session rescheduled.");
        setOpen(false);
        onBooked && onBooked();
        return;
      }
      const bRes = await api.post("/bookings", {
        course_id: course.id, date: iso, time_slot: slot, waiver_name: waiverName.trim(),
      });
      const booking = bRes.data;
      const cRes = await api.post("/payments/checkout", {
        lookup_key: course.lookup_key,
        origin_url: window.location.origin,
        booking_id: booking.id,
      });
      window.location.href = cRes.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong.");
      setLoading(false);
    }
  };

  const activeCourse = course || { name: initialBooking?.course_name, price: initialBooking?.amount, duration: initialBooking?.duration };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-brand-surface border border-white/10 rounded-sm text-brand-ink max-w-lg" data-testid="booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-bold uppercase text-2xl tracking-tight">
            {isReschedule ? "Reschedule" : "Book"} · {activeCourse.name}
          </DialogTitle>
          <DialogDescription className="text-brand-muted text-sm">
            {isReschedule ? "Pick a new date and time for your session." : "Choose a date and time, sign the waiver, then pay securely."}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 flex items-center justify-between border-y border-white/10 py-3">
          <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-brand-muted">
            {activeCourse.duration}
          </span>
          <span className="font-heading font-black text-2xl text-brand-ink">
            ${Number(activeCourse.price).toFixed(0)}
          </span>
        </div>

        <div className="mt-2">
          <label className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-brand-brass mb-3">
            <CalendarDays size={13} /> Select a date
          </label>
          <div className="flex justify-center border border-white/10 rounded-sm p-2">
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
              data-testid="booking-calendar"
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-brand-brass mb-3">
            <Clock size={13} /> Time slot
          </label>
          <Select value={slot} onValueChange={setSlot}>
            <SelectTrigger className="rounded-sm bg-brand-bg border-white/10" data-testid="slot-select">
              <SelectValue placeholder="Choose a time" />
            </SelectTrigger>
            <SelectContent className="bg-brand-surface border-white/10 text-brand-ink">
              {(timeSlots || []).map((t) => (
                <SelectItem key={t} value={t} disabled={blocked.includes(t)} data-testid={`slot-${t}`}>
                  {t}{blocked.includes(t) ? " · unavailable" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {!isReschedule && (
          <div className="mt-4 border border-white/10 rounded-sm p-4 bg-brand-bg/40" data-testid="waiver-block">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-brass">Liability waiver</span>
            <p className="mt-2 text-brand-muted text-xs leading-relaxed max-h-24 overflow-y-auto pr-1">
              {WAIVER_TEXT}
            </p>
            <div className="mt-3 flex items-start gap-3">
              <Checkbox id="waiver-agree" checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} data-testid="waiver-checkbox" className="mt-0.5 rounded-sm border-white/30 data-[state=checked]:bg-brand-orange data-[state=checked]:border-brand-orange" />
              <label htmlFor="waiver-agree" className="text-xs text-brand-ink leading-snug cursor-pointer">
                I have read and agree to the liability waiver above.
              </label>
            </div>
            <input
              value={waiverName}
              onChange={(e) => setWaiverName(e.target.value)}
              placeholder="Type your full legal name to sign"
              data-testid="waiver-name-input"
              className="mt-3 w-full bg-brand-bg border border-white/10 rounded-sm px-3 py-2.5 text-sm text-brand-ink placeholder:text-brand-muted focus:outline-none focus:ring-2 focus:ring-brand-orange"
            />
          </div>
        )}

        <DialogFooter className="mt-4">
          <button
            onClick={handleSubmit}
            disabled={loading || (!isReschedule && (!agreed || !waiverName.trim()))}
            data-testid="booking-submit-btn"
            className="group w-full flex items-center justify-center gap-3 bg-brand-orange text-brand-bg font-mono text-xs uppercase tracking-[0.2em] px-6 py-4 hover:bg-brand-ink transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : (
              <>
                {isReschedule ? "Confirm new time" : "Proceed to secure payment"}
                <ArrowUpRight size={15} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </>
            )}
          </button>
          {!isReschedule && (!agreed || !waiverName.trim()) && (
            <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.15em] text-brand-muted" data-testid="waiver-hint">
              Sign the waiver above to continue
            </p>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
